// Create a deep nested edit view for Slider and its translated items
import React, { useEffect, useState } from 'react';
import { 
    Form, 
    Input, 
    InputNumber, 
    Switch, 
    Button, 
    Card, 
    Space, 
    message, 
    Tabs, 
    Col, 
    Row, 
    Upload, 
    Typography, 
    Image,
    Tag 
} from 'antd';
import { 
    UploadOutlined, 
    PlusOutlined, 
    DeleteOutlined, 
    ArrowLeftOutlined, 
    PictureOutlined 
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/axios';
import MediaPickerModal from '../../components/common/MediaPickerModal';

const { Title } = Typography;

const SliderEdit: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isNew = id === 'new';
    const [form] = Form.useForm();
    const queryClient = useQueryClient();
    const appLanguages = ['de', 'en', 'tr', 'ru', 'ar'];

    const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
    const [selectedFieldForMedia, setSelectedFieldForMedia] = useState<{ index: number; type: 'desktop' | 'mobile' } | null>(null);

    const { data: sliderData, isLoading } = useQuery({
        queryKey: ['adminSlider', id],
        queryFn: async () => {
            const response = await apiClient.get(`/admin/sliders/${id}`);
            return response.data.data;
        },
        enabled: !isNew
    });

    useEffect(() => {
        if (sliderData) {
            // Transform data structure to match form structure
            const formValues = {
                name: sliderData.name,
                autoplaySpeed: sliderData.autoplaySpeed,
                showDots: sliderData.showDots,
                showArrows: sliderData.showArrows,
                items: sliderData.items?.map((item: any) => ({
                    id: item.id,
                    imageUrl: item.imageUrl,
                    mobileImageUrl: item.mobileImageUrl || '',
                    orderIndex: item.orderIndex,
                    translations: item.translations
                })) || []
            };
            form.setFieldsValue(formValues);
        } else if (isNew) {
            form.setFieldsValue({
                autoplaySpeed: 5000,
                showDots: true,
                showArrows: true,
                items: []
            });
        }
    }, [sliderData, isNew, form]);

    const mutation = useMutation({
        mutationFn: async (values: any) => {
            // Transform form items for submission
            const payload = {
                ...values,
                items: values.items?.map((item: any) => ({
                    id: item.id || null,
                    imageUrl: item.imageUrl,
                    mobileImageUrl: item.mobileImageUrl || null,
                    orderIndex: item.orderIndex || 0,
                    translations: appLanguages.reduce((acc: any, lang) => {
                        acc[lang] = item.translations?.[lang] || {};
                        return acc;
                    }, {})

                })) || []
            };

            if (isNew) {
                const res = await apiClient.post('/admin/sliders', payload);
                return res.data;
            } else {
                const res = await apiClient.put(`/admin/sliders/${id}`, { id, ...payload });
                return res.data;
            }
        },
        onSuccess: () => {
            message.success('Slider erfolgreich gespeichert');
            queryClient.invalidateQueries({ queryKey: ['adminSliders'] });
            navigate('/admin/sliders');
        },
        onError: () => {
            message.error('Fehler beim Speichern');
        }
    });

    const onFinish = (values: any) => {
        const items = values.items || [];
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            const hasDesktop = Boolean(item.imageUrl && item.imageUrl.trim() !== '');
            const hasMobile = Boolean(item.mobileImageUrl && item.mobileImageUrl.trim() !== '');
            if (!hasDesktop && !hasMobile) {
                message.error(`Slide #${i + 1} muss mindestens ein Desktop- oder Mobilbild haben.`);
                return;
            }
        }
        mutation.mutate(values);
    };

    const setImageUrlForField = (fieldName: number, imgUrl: string, type: 'desktop' | 'mobile' = 'desktop') => {
        const fieldKey = type === 'mobile' ? 'mobileImageUrl' : 'imageUrl';
        // 1. Direct form field value update for that specific Form.List item
        form.setFieldValue(['items', fieldName, fieldKey], imgUrl);
        // 2. Clear any validation error and set value explicitly
        form.setFields([
            {
                name: ['items', fieldName, fieldKey],
                value: imgUrl,
                errors: [],
            }
        ]);
        // 3. Update clone array so form state matches
        const currentItems = form.getFieldValue('items') || [];
        const updatedItems = [...currentItems];
        updatedItems[fieldName] = { ...updatedItems[fieldName], [fieldKey]: imgUrl };
        form.setFieldsValue({ items: updatedItems });
    };

    const handleCustomRequest = async (options: any, fieldName: number, type: 'desktop' | 'mobile' = 'desktop') => {
        const { file, onSuccess, onError } = options;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('isPublic', 'true');
        formData.append('isActive', 'true');

        try {
            const response = await apiClient.post('/admin/media/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const dataObj = response.data?.data || response.data?.Data || response.data;
            const imgUrl = dataObj?.url || dataObj?.Url || dataObj?.filePath || dataObj?.FilePath;
            
            if (!imgUrl) {
                throw new Error('Keine URL in der Antwort erhalten');
            }

            setImageUrlForField(fieldName, imgUrl, type);

            onSuccess('Ok');
            message.success(type === 'mobile' ? 'Mobilbild hochgeladen' : 'Bild hochgeladen');
            queryClient.invalidateQueries({ queryKey: ['adminMedia'] });
        } catch (error: any) {
            onError({ error });
            message.error(error.response?.data?.message || error.message || 'Upload fehlgeschlagen');
        }
    };

    const handleSelectMedia = (url: string) => {
        if (selectedFieldForMedia !== null) {
            setImageUrlForField(selectedFieldForMedia.index, url, selectedFieldForMedia.type);
        }
        setMediaPickerOpen(false);
        setSelectedFieldForMedia(null);
    };

    if (isLoading && !isNew) return <div>Laden...</div>;

    return (
        <Card 
            title={
                <Space>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/sliders')} />
                    {isNew ? 'Neuen Slider erstellen' : 'Slider bearbeiten'}
                </Space>
            }
        >
            <Form form={form} layout="vertical" onFinish={onFinish}>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="name" label="Interner Name" rules={[{ required: true }]}>
                            <Input placeholder="z.B. Hero Homepage" />
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item name="autoplaySpeed" label="Autoplay Speed (ms)">
                            <InputNumber min={0} className="w-full" />
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item name="showDots" label="Punkte anzeigen" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item name="showArrows" label="Pfeile anzeigen" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </Col>
                </Row>

                <Title level={4} className="mt-8 mb-4">Slides verwalten</Title>
                
                <Form.List name="items">
                    {(fields, { add, remove }) => (
                        <div className="space-y-6 flex flex-col gap-4">
                            {fields.map((field, index) => (
                                <Card size="small" key={field.key} className="bg-gray-50">
                                    <div className="flex justify-between items-center mb-4">
                                        <Space align="center">
                                            <strong>Slide #{index + 1}</strong>
                                            <Form.Item
                                                noStyle
                                                shouldUpdate={(prevValues, currentValues) =>
                                                    prevValues?.items?.[field.name]?.imageUrl !== currentValues?.items?.[field.name]?.imageUrl ||
                                                    prevValues?.items?.[field.name]?.mobileImageUrl !== currentValues?.items?.[field.name]?.mobileImageUrl
                                                }
                                            >
                                                {() => {
                                                    const img = form.getFieldValue(['items', field.name, 'imageUrl']);
                                                    const mob = form.getFieldValue(['items', field.name, 'mobileImageUrl']);
                                                    const hasImg = Boolean(img && img.trim() !== '');
                                                    const hasMob = Boolean(mob && mob.trim() !== '');
                                                    if (hasImg && hasMob) {
                                                        return <Tag color="green">Web &amp; Mobil</Tag>;
                                                    } else if (hasImg) {
                                                        return <Tag color="blue">Nur Web (Desktop)</Tag>;
                                                    } else if (hasMob) {
                                                        return <Tag color="purple">Nur Mobil</Tag>;
                                                    } else {
                                                        return <Tag color="red">Kein Bild gewählt</Tag>;
                                                    }
                                                }}
                                            </Form.Item>
                                        </Space>
                                        <Button danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                                    </div>
                                    
                                    <Form.Item name={[field.name, 'id']} hidden><Input /></Form.Item>
                                    
                                    <Row gutter={24}>
                                        <Col span={8}>
                                            {/* Desktop Image */}
                                            <div className="border-b border-gray-200 pb-3 mb-3">
                                                <Form.Item 
                                                    name={[field.name, 'imageUrl']} 
                                                    label={<span className="font-semibold text-xs">Desktop Bild URL (Web)</span>}
                                                    tooltip="Wird auf Desktop/Web (≥ 768px) angezeigt. Wenn leer, wird dieser Slide auf dem Web nicht angezeigt."
                                                    className="mb-2"
                                                >
                                                    <Input placeholder="https://... oder Bild hochladen" allowClear />
                                                </Form.Item>
                                                
                                                <Space wrap className="mb-2">
                                                    <Upload 
                                                        customRequest={(options) => handleCustomRequest(options, field.name, 'desktop')} 
                                                        showUploadList={false}
                                                    >
                                                        <Button size="small" icon={<UploadOutlined />}>Hochladen</Button>
                                                    </Upload>
                                                    <Button 
                                                        size="small"
                                                        icon={<PictureOutlined />}
                                                        onClick={() => {
                                                            setSelectedFieldForMedia({ index: field.name, type: 'desktop' });
                                                            setMediaPickerOpen(true);
                                                        }}
                                                    >
                                                        Mediathek
                                                    </Button>
                                                    <Button 
                                                        size="small" 
                                                        danger
                                                        onClick={() => setImageUrlForField(field.name, '', 'desktop')}
                                                    >
                                                        Löschen
                                                    </Button>
                                                </Space>

                                                {/* Preview Desktop Image */}
                                                <Form.Item
                                                    noStyle
                                                    shouldUpdate={(prevValues, currentValues) =>
                                                        prevValues?.items?.[field.name]?.imageUrl !== currentValues?.items?.[field.name]?.imageUrl
                                                    }
                                                >
                                                    {() => {
                                                        const currentUrl = form.getFieldValue(['items', field.name, 'imageUrl']);
                                                        if (!currentUrl) return null;
                                                        return (
                                                            <div className="mt-1">
                                                                <Image
                                                                    src={currentUrl}
                                                                    alt={`Slide #${index + 1} Desktop`}
                                                                    style={{ maxHeight: 80, maxWidth: '100%', objectFit: 'cover', borderRadius: 4, border: '1px solid #d9d9d9' }}
                                                                />
                                                            </div>
                                                        );
                                                    }}
                                                </Form.Item>
                                            </div>

                                            {/* Mobile Image (Optional) */}
                                            <div className="border-b border-gray-200 pb-3 mb-3">
                                                <Form.Item 
                                                    name={[field.name, 'mobileImageUrl']} 
                                                    label={<span className="font-semibold text-xs text-blue-700">Mobil Bild URL (&lt; 768px)</span>}
                                                    className="mb-2"
                                                    tooltip="Wird auf Smartphones (< 768px) angezeigt. Wenn leer, wird dieser Slide auf Mobilgeräten nicht angezeigt."
                                                >
                                                    <Input placeholder="Mobilversion URL..." allowClear />
                                                </Form.Item>
                                                
                                                <Space wrap className="mb-2">
                                                    <Upload 
                                                        customRequest={(options) => handleCustomRequest(options, field.name, 'mobile')} 
                                                        showUploadList={false}
                                                    >
                                                        <Button size="small" icon={<UploadOutlined />}>Mobil Hochladen</Button>
                                                    </Upload>
                                                    <Button 
                                                        size="small"
                                                        icon={<PictureOutlined />}
                                                        onClick={() => {
                                                            setSelectedFieldForMedia({ index: field.name, type: 'mobile' });
                                                            setMediaPickerOpen(true);
                                                        }}
                                                    >
                                                        Mediathek
                                                    </Button>
                                                    <Button 
                                                        size="small" 
                                                        danger
                                                        onClick={() => setImageUrlForField(field.name, '', 'mobile')}
                                                    >
                                                        Löschen
                                                    </Button>
                                                </Space>

                                                {/* Preview Mobile Image */}
                                                <Form.Item
                                                    noStyle
                                                    shouldUpdate={(prevValues, currentValues) =>
                                                        prevValues?.items?.[field.name]?.mobileImageUrl !== currentValues?.items?.[field.name]?.mobileImageUrl
                                                    }
                                                >
                                                    {() => {
                                                        const currentUrl = form.getFieldValue(['items', field.name, 'mobileImageUrl']);
                                                        if (!currentUrl) return null;
                                                        return (
                                                            <div className="mt-1">
                                                                <Image
                                                                    src={currentUrl}
                                                                    alt={`Slide #${index + 1} Mobil`}
                                                                    style={{ maxHeight: 80, maxWidth: '100%', objectFit: 'cover', borderRadius: 4, border: '1px solid #1677ff' }}
                                                                />
                                                            </div>
                                                        );
                                                    }}
                                                </Form.Item>
                                            </div>

                                            <Form.Item name={[field.name, 'orderIndex']} label="Sortierung" className="mt-2">
                                                <InputNumber min={0} className="w-full" />
                                            </Form.Item>
                                        </Col>
                                        
                                        <Col span={16}>
                                             <Tabs
                                                items={appLanguages.map(lang => ({
                                                    key: lang,
                                                    label: lang === 'de' ? 'Deutsch' : lang === 'tr' ? 'Türkçe' : lang === 'en' ? 'English' : lang === 'ru' ? 'Русский' : 'العربية',
                                                    children: (
                                                        <div className="space-y-4">
                                                            <Form.Item name={[field.name, 'translations', lang, 'title']} label="Titel / Title">
                                                                <Input />
                                                            </Form.Item>
                                                            <Form.Item name={[field.name, 'translations', lang, 'subtitle']} label="Untertitel / Subtitle">
                                                                <Input />
                                                            </Form.Item>
                                                            <Row gutter={16}>
                                                                <Col span={12}>
                                                                    <Form.Item name={[field.name, 'translations', lang, 'buttonText']} label="Button Text">
                                                                        <Input />
                                                                    </Form.Item>
                                                                </Col>
                                                                <Col span={12}>
                                                                    <Form.Item name={[field.name, 'translations', lang, 'buttonUrl']} label="Button URL">
                                                                        <Input placeholder="/projekte" />
                                                                    </Form.Item>
                                                                </Col>
                                                            </Row>
                                                        </div>
                                                    )
                                                }))}
                                            />

                                        </Col>
                                    </Row>
                                </Card>
                            ))}
                            
                            <Button type="dashed" onClick={() => add({ orderIndex: fields.length + 1 })} block icon={<PlusOutlined />}>
                                Slide hinzufügen
                            </Button>
                        </div>
                    )}
                </Form.List>

                <div className="mt-8 flex justify-end">
                    <Button type="primary" htmlType="submit" size="large" loading={mutation.isPending}>
                        Speichern
                    </Button>
                </div>
            </Form>

            <MediaPickerModal
                open={mediaPickerOpen}
                onCancel={() => {
                    setMediaPickerOpen(false);
                    setSelectedFieldForMedia(null);
                }}
                onSelect={handleSelectMedia}
            />
        </Card>
    );
};

export default SliderEdit;
